/* eslint-disable @typescript-eslint/naming-convention */
import axios from 'axios';
import {
  IClientCompany,
  ICompany,
  ICompanyProfile,
  ICredentials,
  IInsurance,
  IInvoice,
  IOtherProduct,
  IPaginationResponse,
  IPartner,
  IPromotion,
  IReceipt,
  IService,
  ITestimonial,
  ITourPackage,
} from '../types/types';
import getErrorMessage from './error';

const baseUrl = 'https://api.msatravelservices.com/api';
const baseUrlWilayah = 'https://alamat.thecloudalert.com/api';

const api = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
});

export async function login(loginData: ICredentials) {
  try {
    const response = await api.post(`${baseUrl}/auth/login`, loginData);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

export async function logout() {
  try {
    const response = await api.post(`${baseUrl}/auth/logout`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

export async function getStatusAuth() {
  try {
    const response = await api.get(`${baseUrl}/auth/status`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

export async function getUserAuth() {
  try {
    const response = await api.get(`${baseUrl}/me`);
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

export async function getCompanyProfile() {
  try {
    const response = await api.get(`${baseUrl}/profiles`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function updateCompanyProfile(profileData: ICompanyProfile) {
  try {
    const response = await api.put(`${baseUrl}/profiles/`, profileData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getClientCompanies() {
  try {
    const response = await api.get(`${baseUrl}/partner-companies`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function createClientCompany(clientData: IClientCompany) {
  try {
    const response = await api.post(`${baseUrl}/partner-companies`, clientData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function updateClientCompany(id: number, clientData: IClientCompany) {
  try {
    const response = await api.put(`${baseUrl}/partner-companies/${id}`, clientData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function deleteClientCompany(id: number) {
  try {
    const response = await api.delete(`${baseUrl}/partner-companies/${id}`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getServices() {
  try {
    const response = await api.get(`${baseUrl}/services`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getServiceById(id: number) {
  try {
    const response = await api.get(`${baseUrl}/services/${id}`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function createService(serviceData: IService) {
  try {
    const response = await api.post(`${baseUrl}/services`, serviceData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function updateService(id: number, serviceData: IService) {
  try {
    const response = await api.put(`${baseUrl}/services/${id}`, serviceData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function deleteService(id: number) {
  try {
    const response = await api.delete(`${baseUrl}/services/${id}`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getPromotions(
  page: number | null = null,
  limit: number | null = null,
): Promise<IPaginationResponse> {
  try {
    const response = await api.get<IPaginationResponse>(`${baseUrl}/promotions`, {
      params: { page, limit },
    });
    const { message, success, data, meta } = response.data;
    if (success) {
      return { message, success, data, meta };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error), data: [], meta: { totalItems: 0, paginated: false } };
  }
}

export async function getPromotionByServiceId(
  page: number | null = null,
  limit: number | null = null,
  serviceId: number | null = null,
): Promise<IPaginationResponse> {
  try {
    const response = await api.get<IPaginationResponse>(`${baseUrl}/promotions/srv`, {
      params: { page, limit, serviceId },
    });
    const { message, success, data, meta } = response.data;
    if (success) {
      return { message, success, data, meta };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error), data: [], meta: { totalItems: 0, paginated: false } };
  }
}

export async function createPromotion(promotionData: IPromotion) {
  try {
    const response = await api.post(`${baseUrl}/promotions`, promotionData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function updatePromotion(id: number, promotionData: Partial<IPromotion>) {
  try {
    const response = await api.put(`${baseUrl}/promotions/${id}`, promotionData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function deletePromotion(id: number) {
  try {
    const response = await api.delete(`${baseUrl}/promotions/${id}`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getProducts(
  page: number | null = null,
  limit: number | null = null,
): Promise<IPaginationResponse> {
  try {
    const response = await api.get<IPaginationResponse>(`${baseUrl}/products`, {
      params: { page, limit },
    });
    const { message, success, data, meta } = response.data;
    if (success) {
      return { message, success, data, meta };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error), data: [], meta: { totalItems: 0, paginated: false } };
  }
}

export async function getProductsByServiceId(
  page: number | null = null,
  limit: number | null = null,
  serviceId: number | null = null,
): Promise<IPaginationResponse> {
  try {
    const response = await api.get<IPaginationResponse>(`${baseUrl}/products/srv`, {
      params: { page, limit, serviceId },
    });
    const { message, success, data, meta } = response.data;
    if (success) {
      return { message, success, data, meta };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error), data: [], meta: { totalItems: 0, paginated: false } };
  }
}

export async function createProduct(productData: IOtherProduct) {
  try {
    const response = await api.post(`${baseUrl}/products`, productData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function updateProduct(id: number, productData: IOtherProduct) {
  try {
    const response = await api.put(`${baseUrl}/products/${id}`, productData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function deleteProduct(id: number) {
  try {
    const response = await api.delete(`${baseUrl}/products/${id}`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getInsurances() {
  try {
    const response = await api.get(`${baseUrl}/insurances`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function createInsurance(insuranceData: IInsurance) {
  try {
    const response = await api.post(`${baseUrl}/insurances`, insuranceData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function updateInsurance(id: number, insuranceData: IInsurance) {
  try {
    const response = await api.put(`${baseUrl}/insurances/${id}`, insuranceData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function deleteInsurance(id: number) {
  try {
    const response = await api.delete(`${baseUrl}/insurances/${id}`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getTestimonials() {
  try {
    const response = await api.get(`${baseUrl}/testimonials`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function createTestimonial(testimonialData: ITestimonial) {
  try {
    const response = await api.post(`${baseUrl}/testimonials`, testimonialData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function updateTestimonial(id: number, testimonialData: ITestimonial) {
  try {
    const response = await api.put(`${baseUrl}/testimonials/${id}`, testimonialData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function deleteTestimonial(id: number) {
  try {
    const response = await api.delete(`${baseUrl}/testimonials/${id}`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getTours(page: number | null = null, limit: number | null = null): Promise<IPaginationResponse> {
  try {
    const response = await api.get<IPaginationResponse>(`${baseUrl}/tour-packages`, {
      params: { page, limit },
    });
    const { message, success, data, meta } = response.data;
    if (success) {
      return { message, success, data, meta };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error), data: [], meta: { totalItems: 0, paginated: false } };
  }
}

export async function getTourById(id: number) {
  try {
    const response = await api.get(`${baseUrl}/tour-packages/${id}`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function createTour(tourData: ITourPackage) {
  try {
    const response = await api.post(`${baseUrl}/tour-packages`, tourData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function updateTour(id: number, tourData: ITourPackage) {
  try {
    const response = await api.put(`${baseUrl}/tour-packages/${id}`, tourData);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function deleteTour(id: number) {
  try {
    const response = await api.delete(`${baseUrl}/tour-packages/${id}`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function uploadImageToCloudinary(file: File, onProgress: (progress: number) => void): Promise<string> {
  const cloudName = 'dpgwrk4w8';
  const uploadPreset = 'msatravelservices';

  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary credentials are not set');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);

  try {
    const response = await axios.post(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, formData, {
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        onProgress(percent);
      },
    });

    return response.data.secure_url;
  } catch (error) {
    throw getErrorMessage(error);
  }
}

//* Digital Invoice Api
export async function uploadImage(file: File, onUploadProgress?: (progress: number) => void): Promise<string> {
  const cloudName = 'dpgwrk4w8';
  const uploadPreset = 'msatravelservices';

  if (!cloudName || !uploadPreset) {
    throw new Error('Cloudinary credentials are not set');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);

  try {
    const response = await axios.post(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, formData, {
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onUploadProgress(percentCompleted);
        }
      },
    });

    return response.data.secure_url;
  } catch (error) {
    const message = getErrorMessage(error);
    throw message;
  }
}

// All Invoice
export async function getAllInvoice(query: string, signal?: AbortSignal) {
  try {
    const response = await api.get(`${baseUrl}/invoice?${query}`, { signal });
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Invoice By ID
export async function getInvoiceById(id: string) {
  try {
    const response = await api.get(`${baseUrl}/invoice/${id}`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Create Invoice
export async function createInvoice(data: IInvoice) {
  try {
    const response = await api.post(`${baseUrl}/invoice/`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Update Invoice
export async function updateInvoice(id: string, data: IInvoice) {
  try {
    const response = await api.patch(`${baseUrl}/invoice/${id}`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Delete Invoice
export async function deleteInvoice(id: string) {
  try {
    const response = await api.delete(`${baseUrl}/invoice/${id}`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// All Contact
export async function getAllContact() {
  try {
    const response = await api.get(`${baseUrl}/contact`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// All Partner
export async function getAllPartner(query: string, signal?: AbortSignal) {
  try {
    const response = await api.get(`${baseUrl}/partner?${query}`, { signal });
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Partner By ID
export async function getPartnerById(id: string) {
  try {
    const response = await api.get(`${baseUrl}/partner/${id}`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Create Partner
export async function createPartner(data: IPartner) {
  try {
    const response = await api.post(`${baseUrl}/partner/`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Update Partner
export async function updatePartner(id: string, data: IPartner) {
  try {
    const response = await api.patch(`${baseUrl}/partner/${id}`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Delete Partner
export async function deletePartner(id: string) {
  try {
    const response = await api.delete(`${baseUrl}/partner/${id}`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Update Company
export async function updateCompany(id: string, data: ICompany) {
  try {
    const response = await api.patch(`${baseUrl}/company/${id}`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// All Receipt
export async function getAllReceipt(query: string, signal?: AbortSignal) {
  try {
    const response = await api.get(`${baseUrl}/receipt?${query}`, { signal });
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Receipt By ID
export async function getReceiptById(id: string) {
  try {
    const response = await api.get(`${baseUrl}/receipt/${id}`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Create Receipt
export async function createReceipt(data: IReceipt) {
  try {
    const response = await api.post(`${baseUrl}/receipt/`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Update Receipt
export async function updateReceipt(id: string, data: IReceipt) {
  try {
    const response = await api.patch(`${baseUrl}/receipt/${id}`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// Delete Receipt
export async function deleteReceipt(id: string) {
  try {
    const response = await api.delete(`${baseUrl}/receipt/${id}`);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// All Receipt Detail by Receipt ID
export async function getAllReceiptDetail(id: string) {
  try {
    const response = await api.get(`${baseUrl}/receiptDetail/${id}`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// All Provinsi
export async function getAllProvinsi() {
  try {
    const response = await axios.get(`${baseUrlWilayah}/provinsi/get`);
    const { result, message, status } = response.data;
    if (status === 200) {
      return { data: result, message, success: true };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// All City
export async function getAllCity(provinsiId: string) {
  try {
    const response = await axios.get(`${baseUrlWilayah}/kabkota/get/?d_provinsi_id=${provinsiId}`);
    const { result, message, status } = response.data;
    if (status === 200) {
      return { data: result, message, success: true };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

// All District
export async function getAllDistrict(kabId: string) {
  try {
    const response = await axios.get(`${baseUrlWilayah}/kecamatan/get/?d_kabkota_id=${kabId}`);
    const { result, message, status } = response.data;
    if (status === 200) {
      return { data: result, message, success: true };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

export async function sendEmailVerify(id: string, data: { newEmail: string }) {
  try {
    const response = await api.post(`${baseUrl}/users/${id}/email-change`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

export async function verifyOtp(id: string, data: { otp: string }) {
  try {
    const response = await api.post(`${baseUrl}/users/${id}/email-change/verify`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

export async function changeEmail(id: string, data: { newEmail: string }) {
  try {
    const response = await api.post(`${baseUrl}/users/${id}/email-change`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

export async function updateNameUser(id: string, data: { name: string }) {
  try {
    const response = await api.patch(`${baseUrl}/users/${id}/`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

export async function updatePassword(
  id: string,
  data: { oldPassword: string; newPassword: string; confirmPassword: string },
) {
  try {
    const response = await api.patch(`${baseUrl}/auth/${id}/change-pass`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success: true };
    }
    throw new Error(message);
  } catch (error) {
    const message = getErrorMessage(error);
    return { message, success: false };
  }
}

export async function getMonthlyRevenue(id: string) {
  try {
    const response = await api.get(`${baseUrl}/users/${id}/analytics/monthly-revenue`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getCurrentMonthRevenue(id: string) {
  try {
    const response = await api.get(`${baseUrl}/users/${id}/analytics/current-month`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getAccountsReceivable(id: string) {
  try {
    const response = await api.get(`${baseUrl}/users/${id}/analytics/accounts-receivable`);
    const { message, success, data } = response.data;
    if (success) {
      return { message, success, data };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function getResetEmail(data: { email: string }) {
  try {
    const response = await api.post(`${baseUrl}/users/forgot-password`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}

export async function resetPassword(data: { token: string; newPassword: string }) {
  try {
    const response = await api.post(`${baseUrl}/users/reset-password`, data);
    const { message, success } = response.data;
    if (success) {
      return { message, success };
    }
    throw new Error(message);
  } catch (error) {
    return { success: false, message: getErrorMessage(error) };
  }
}
